import React, { useState } from 'react'
import { redeemRates, giftCardAllowed, amazonPayLogo } from '@egreact/shared'
import './Referral.css'

// The backend's COIN_SOURCE_REFERRAL, the lot source a referral reward
// lands in.
const REFERRAL_COIN_SOURCE = 'referral'

// Shown until the programme answers, and kept if it never does. The page
// is advertising, so it has to say something rather than a blank space.
const FALLBACK_MAX_COINS = 10000
const FALLBACK_COIN_VALUE = 1

const inr = (value) => `₹${Number(value).toLocaleString('en-IN')}`
const coins = (value) => Number(value).toLocaleString('en-IN')

// Bands are open ended at both ends: the first has no floor worth saying
// and the last has no ceiling at all.
const bandLabel = (band) => {
  if (band.max_amount === null) return `${inr(band.min_amount)} and above`
  if (band.min_amount === null || Number(band.min_amount) <= 0) {
    return `Up to ${inr(band.max_amount)}`
  }
  return `${inr(band.min_amount)} - ${inr(band.max_amount)}`
}

// A band pays a percentage of the booking capped in coins, or, on the
// older bands, a flat count. Same order the credit itself reads them in.
const referrerLabel = (band) => (
  band.referrer_pct !== null
    ? `${Number(band.referrer_pct)}% up to ${coins(band.referrer_cap_coins)} coins`
    : `${coins(band.referrer_flat_coins)} coins`
)

const refereeLabel = (band) => (
  `${Number(band.referee_pct)}% up to ${inr(band.referee_cap)} discount`
)

// The referrer's headline is the most the live programme will actually pay:
// the best ceiling across its bands, read the same way the credit reads them
// - a percentage band is capped in coins, an older flat band pays its count
// outright. Derived rather than taken on trust so the number on the page can
// never outrun the slabs behind the (i).
const topReferrerCoins = (bands) => {
  const ceilings = bands
    .map((band) => (
      band.referrer_pct !== null ? band.referrer_cap_coins : band.referrer_flat_coins
    ))
    .filter((value) => value !== null && value !== undefined && value !== '')
    .map(Number)
    .filter(Number.isFinite)
  return ceilings.length ? Math.max(...ceilings) : null
}

/**
 * The reward band: the most a referral pays, what that is as a voucher,
 * the booking floor, and the slabs behind the (i). Shared by the referral
 * page and the wallet, so both quote the same figure.
 *
 * ``hero`` is the page's headline, which the band carries on mobile. The
 * wallet passes none.
 */
const ReferralRewardCard = ({ programme, hero = null }) => {
  const [showSlabs, setShowSlabs] = useState(false)

  const bands = programme?.bands || []
  const maxCoins = topReferrerCoins(bands)
    ?? programme?.max_reward_coins
    ?? FALLBACK_MAX_COINS
  const coinValue = programme?.coin_value_inr || FALLBACK_COIN_VALUE
  const maxRupees = maxCoins * coinValue
  // The same coins spent off the platform. Read through the wallet's own
  // helper so this page and the redeem sheet quote one rate, and shown
  // only where it differs from the figure above - at parity it would be
  // the headline again with a logo beside it.
  const voucherRate = redeemRates(programme?.redeem_rates).giftCard
  const maxVoucherRupees = maxCoins * voucherRate
  // Referral coins only buy a voucher while ops allow that source.
  const showVoucherWorth = maxVoucherRupees > 0
    && maxVoucherRupees !== maxRupees
    && giftCardAllowed(programme?.redeem_limits, REFERRAL_COIN_SOURCE)
  // Only when the programme actually has a floor. The backend sends null
  // when the lowest band is open ended, so there is nothing to say.
  const minBooking = programme?.min_booking_amount || null

  return (
  <>
    {/* Reward line */}
    <div className="ref-reward">
      {hero && <div className="ref-reward-hero">{hero}</div>}
      <div className="ref-reward-main">
        <div className="ref-reward-coins" aria-hidden="true">
          <span className="ref-coin ref-coin--back" />
          <span className="ref-coin ref-coin--mid" />
          <span className="ref-coin ref-coin--front">EG</span>
        </div>
        <div className="ref-reward-copy">
          <span className="ref-reward-eyebrow">Earn up to</span>
          <div className="ref-reward-amount">
            <span className="ref-reward-number">{inr(maxRupees)}</span>
          </div>
          <span className="ref-reward-per">per referral</span>
        </div>
        {bands.length > 0 && (
          <button
            type="button"
            className="ref-reward-info"
            aria-label="See reward slabs"
            onClick={() => setShowSlabs(true)}
          >
            i
          </button>
        )}
      </div>
      {/* Its own centred line under the figure. The logo stands in for
          the brand name rather than sitting beside it, so it carries
          the alt text a reader needs for the sentence to finish. */}
      {showVoucherWorth && (
        <div className="ref-reward-alt">
          or up to {inr(maxVoucherRupees)} as an
          <span className="ref-reward-alt-logo">
            <img src={amazonPayLogo} alt="Amazon Pay" />
          </span>
          voucher
        </div>
      )}
      {minBooking && (
        <p className="ref-reward-note">
          On bookings of {inr(minBooking)} and above
        </p>
      )}
    </div>

    {showSlabs && (
      <div
        className="ref-slabs-backdrop"
        role="presentation"
        onClick={() => setShowSlabs(false)}
      >
        <div
          className="ref-slabs"
          role="dialog"
          aria-modal="true"
          aria-label="Reward slabs"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ref-slabs-head">
            <div>
              <div className="ref-slabs-eyebrow">Reward slabs</div>
              <h3 className="ref-slabs-title">What each booking earns</h3>
            </div>
            <button
              type="button"
              className="ref-slabs-close"
              aria-label="Close"
              onClick={() => setShowSlabs(false)}
            >
              &times;
            </button>
          </div>

          {/* The slabs are quoted in coins, so the sheet states what a coin
              is worth before the reader reaches them - the same chip the
              wallet card wears. */}
          <div
            className="ref-rate-chip"
            title={`1 EG Coin = ${inr(coinValue)} if used on EG platform`}
          >
            <span className="ref-rate-chip__coin" aria-hidden="true">EG</span>
            <span className="ref-rate-chip__text">1 EG</span>
            <span className="ref-rate-chip__eq">=</span>
            <span className="ref-rate-chip__rupee">{inr(coinValue)}</span>
          </div>

          <div className="ref-slabs-rows">
            {bands.map((band, index) => (
              <div className="ref-slab" key={index}>
                <div className="ref-slab-band">Booking value: {bandLabel(band)}</div>
                <div className="ref-slab-split">
                  <div className="ref-slab-half ref-slab-half--you">
                    <span className="ref-slab-who">You get</span>
                    <span className="ref-slab-value">{referrerLabel(band)}</span>
                  </div>
                  <div className="ref-slab-half ref-slab-half--friend">
                    <span className="ref-slab-who">Your friend gets</span>
                    <span className="ref-slab-value">{refereeLabel(band)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="ref-slabs-foot">
            The slab is decided by your friend's booking value.
          </p>
        </div>
      </div>
    )}
  </>
  )
}

export default ReferralRewardCard
